import React from "react";

import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import useDatasetMappedValue from "hooks/useDatasetMappedValue";
import { DatasetDataInfo } from "models/dataset-models";
import { formatDatasetValue } from "utils/value-format-utils";
import { useDatasetMappedIcon } from "hooks/useDatasetMappedIcon";
import { I } from "services/lang/LanguageService";
import { styleHandler } from "components/dashboard/product-template-table/utils/utils";
import {
  getArrowDirection,
  getDiffClass,
} from "components/dashboard/value-change-indicator/ValueChangeIndicatorUtils";

type DatasetDisplayValueProps = {
  dataset: DatasetDataInfo | undefined;
  data: InstantDoubleValue | null | undefined;
  loading: boolean;
  isData: boolean;
  truncateSize?: number;
  isInScada?: boolean;
  diffValue?: number;
  diffDecimalPlaces?: number;
  isDiffPercentageVisible?: boolean;
};

const DatasetDisplayValue: React.FC<DatasetDisplayValueProps> = ({
  dataset,
  data,
  loading,
  isData,
  truncateSize,
  isInScada,
  diffValue,
  isDiffPercentageVisible,
  diffDecimalPlaces = 2,
}) => {
  if (loading || !dataset) {
    return (
      <span className="px-3 py-1">
        <i className="pi pi-spin pi-spinner" />
      </span>
    );
  }

  if (!isData) {
    return (
      <>
        <span className="font-bold">N/A</span>
        <span className="font-italic"> {dataset.unit}</span>
      </>
    );
  }

  const currentTruncateSize = isInScada ? (truncateSize ?? 10) : undefined;

  if (dataset.stringValueMapper) {
    return (
      <MappedValue
        mapValue={data?.value}
        mapperId={dataset.stringValueMapper}
        truncateSize={currentTruncateSize}
      />
    );
  }

  const isNegative = diffValue ? diffValue < 0 : false;
  const isNeutral = diffValue && Number(diffValue.toFixed(diffDecimalPlaces)) === 0;
  return (
    <>
      <span className="font-bold">{formatDatasetValue(dataset, data)}</span>
      <span className="font-italic"> {dataset.unit}</span>
      {diffValue && (
        <>
          {isNeutral && <i className="pi pi-arrows-v ml-2" />}
          {!isNeutral && (
            <i
              className={`pi pi-arrow-${getArrowDirection(isNegative)} ${getDiffClass(isNegative)} text-sm ml-2`}
            />
          )}
          {diffValue && isDiffPercentageVisible && ` ${diffValue.toFixed(diffDecimalPlaces)}%`}
        </>
      )}
    </>
  );
};

export default DatasetDisplayValue;

type MappedValueProps = {
  mapValue: number | undefined;
  mapperId: number;
  truncateSize?: number;
  color?: string;
  bgColor?: string;
};

export const MappedValue: React.FC<MappedValueProps> = ({
  mapValue,
  mapperId,
  color,
  bgColor,
  truncateSize,
}) => {
  const { value: mappedValue, isLoading: mappedValueLoading } = useDatasetMappedValue(mapValue, mapperId);
  const mappedIcon = useDatasetMappedIcon(mapValue, mapperId);

  if (mappedIcon) {
    return <i style={styleHandler(color, bgColor)} className={mappedIcon as string} />;
  }

  if (!mappedValue && !mappedValueLoading) {
    return (
      <div style={styleHandler(color, bgColor)} className="px-3 py-1">
        {mapValue} <i className="pi pi-info-circle" />
      </div>
    );
  }
  if (mappedValueLoading) {
    return (
      <div className="px-3 py-1">
        <i className="pi pi-spin pi-spinner" />
      </div>
    );
  }

  return (
    <div
      className="truncate-text-mapped-value"
      style={{
        maxWidth: truncateSize && `${truncateSize}ch`,
        color,
        backgroundColor: bgColor,
      }}
    >
      {typeof mappedValue === "string" ? I(mappedValue) : mappedValue}
    </div>
  );
};
